import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown } from "lucide-react";
import client, { fmtPct } from "../lib/api";
import Sparkline from "../components/Sparkline";

const fmtCcy = (n, currency) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return n.toFixed(2);
  }
};

const StockCard = ({ s }) => {
  // For TSX symbols (.TO), Yahoo returns the bare ticker (e.g., RY) for our /asset/stock route
  // which won't resolve via Alpha Vantage. Link to OUR detail page only for US tickers.
  const isTsx = s.symbol.endsWith(".TO");
  const inner = (
    <>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-white/[0.06] border border-white/10 flex items-center justify-center font-display text-xs">
          {s.symbol.replace(".TO", "").slice(0, 4)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">{s.name}</div>
          <div className="text-[10px] text-neutral-500 uppercase tracking-widest font-mono-num">{s.symbol}</div>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono-num text-lg">{fmtCcy(s.price, s.currency)}</div>
          <div className={`text-xs font-mono-num inline-flex items-center gap-1 ${s.change_percent >= 0 ? "up" : "down"}`}>
            {s.change_percent >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
            {s.change_percent !== null && s.change_percent !== undefined ? fmtPct(s.change_percent) : "—"}
          </div>
        </div>
        {s.history?.length > 1 && <Sparkline data={s.history} width={86} height={32} />}
      </div>
    </>
  );

  if (isTsx) {
    return (
      <div data-testid={`stock-card-${s.symbol}`} className="glass glass-hover p-4">
        {inner}
      </div>
    );
  }
  return (
    <Link to={`/asset/stock/${s.symbol}`} data-testid={`stock-card-${s.symbol}`} className="glass glass-hover p-4 block">
      {inner}
    </Link>
  );
};

const Stocks = () => {
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);
  const [exchange, setExchange] = useState("ALL"); // ALL | US | TSX
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetchBoard = () => {
      client.get("/stocks/board").then(({ data }) => {
        if (!cancelled) setBoard(data || {});
        setLoading(false);
      }).catch((e) => {
        console.error("stocks board failed", e);
        if (!cancelled) setLoading(false);
      });
    };
    fetchBoard();
    const id = setInterval(fetchBoard, 60_000); // refresh every 60s
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const filteredBoard = useMemo(() => {
    const out = {};
    for (const [cat, items] of Object.entries(board)) {
      const isTsxCat = cat.toLowerCase().startsWith("tsx");
      if (exchange === "US" && isTsxCat) continue;
      if (exchange === "TSX" && !isTsxCat) continue;
      const filtered = query
        ? items.filter((x) => (x.name + " " + x.symbol).toLowerCase().includes(query.toLowerCase()))
        : items;
      if (filtered.length) out[cat] = filtered;
    }
    return out;
  }, [board, exchange, query]);

  const categories = Object.keys(filteredBoard);
  const total = Object.values(filteredBoard).reduce((a, v) => a + v.length, 0);

  return (
    <div className="space-y-8 fade-in-up" data-testid="stocks-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <span className="chip">Equities</span>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">Stocks Board</h1>
          <p className="text-neutral-400 mt-2 text-sm">Live data from NYSE, NASDAQ & TSX · {total} tickers across {categories.length} sectors.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {["ALL", "US", "TSX"].map((e) => (
            <button
              key={e}
              data-testid={`exchange-filter-${e}`}
              onClick={() => setExchange(e)}
              className={`chip ${exchange === e ? "!bg-white !text-black !border-white" : ""}`}
            >
              {e === "ALL" ? "All Markets" : e === "US" ? "NYSE / NASDAQ" : "TSX"}
            </button>
          ))}
          <input
            data-testid="stocks-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter…"
            className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm w-40 focus:outline-none focus:border-white/20"
          />
        </div>
      </div>

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={`sk-${i}`} className="glass h-32 animate-pulse" />)}
        </div>
      )}

      {!loading && categories.length === 0 && (
        <div className="glass p-12 text-center text-neutral-500">No tickers match your filters.</div>
      )}

      {!loading && categories.map((cat) => (
        <section key={cat} data-testid={`stocks-section-${cat}`}>
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-xl tracking-tight">{cat}</h2>
            <div className="text-xs text-neutral-500 font-mono-num">{filteredBoard[cat].length} tickers</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredBoard[cat].map((s) => <StockCard key={s.symbol} s={s} />)}
          </div>
        </section>
      ))}
    </div>
  );
};

export default Stocks;
